import React from 'react';
import { Box, Link } from "@mui/material";
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { tokens } from '../../Components/DashboardTheme/DashboardTheme';
import SectionHeading from '../../Components/SectionHeading/SectionHeading';
import Reveal from '../../Components/Reveal/Reveal';
import { SponsorData } from './SponsorData';

export default function SponsorPage() {
    const scrollToContatti = () => {
        document.getElementById('contattiSection')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <Box component="section" id="sponsorSection" sx={{ backgroundColor: tokens.ink }}>
            <SectionHeading
                kicker="Chi ci sostiene"
                title="PARTNERSHIP"
                ghost="SPONSOR"
                lead="Il nostro obiettivo è promuovere l'eccellenza sportiva e offrire spettacoli emozionanti sul ring. Grazie a chi ci cammina accanto."
            />

            <Box
                sx={{
                    display: 'grid',
                    // auto-fit keeps the row balanced whatever the sponsor count.
                    gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))',
                    gap: { xs: 2, md: 2.5 },
                    maxWidth: 1280,
                    mx: 'auto',
                    px: { xs: 2.5, md: 6 },
                    pb: { xs: 7, md: 12 },
                }}
            >
                {SponsorData.map((sponsor, i) => (
                    <Reveal key={sponsor.id} delay={i * 100}>
                        <Link
                            href={sponsor.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="none"
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                overflow: 'hidden',
                                backgroundColor: tokens.surface,
                                border: `1px solid ${tokens.line}`,
                                p: { xs: 2.5, md: 3 },
                                transition: `all 0.45s ${tokens.ease}`,
                                ':hover': {
                                    borderColor: tokens.red,
                                    transform: 'translateY(-5px)',
                                    boxShadow: `0 18px 44px rgba(0,0,0,0.5)`,
                                },
                                ':hover .vg-sponsor-plate': { transform: 'scale(1.02)' },
                                ':hover .vg-sponsor-cta': { color: tokens.white, gap: '12px' },
                                ':hover .vg-sponsor-glow': { opacity: 1 },
                            }}
                        >
                            {/* Red bloom on hover */}
                            <Box
                                className="vg-sponsor-glow"
                                aria-hidden
                                sx={{
                                    position: 'absolute',
                                    top: -70,
                                    right: -70,
                                    width: 190,
                                    height: 190,
                                    background: `radial-gradient(circle, ${tokens.red}40 0%, transparent 70%)`,
                                    opacity: 0,
                                    transition: `opacity 0.5s ${tokens.ease}`,
                                    pointerEvents: 'none',
                                }}
                            />

                            <Box
                                sx={{
                                    position: 'relative',
                                    fontFamily: tokens.heading,
                                    fontSize: 12,
                                    letterSpacing: '0.26em',
                                    color: tokens.red,
                                    mb: 2,
                                }}
                            >
                                {String(i + 1).padStart(2, '0')}
                            </Box>

                            {/* The supplied logos are raster files on white, and the
                                C4U wordmark is black — both need a light plate. */}
                            <Box
                                className="vg-sponsor-plate"
                                sx={{
                                    position: 'relative',
                                    // flex e non grid: con place-items:center la riga di
                                    // griglia si dimensiona sul contenuto, quindi il
                                    // max-height:100% dell'immagine non risolve e i loghi
                                    // quadrati sforavano la placca.
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: { xs: 130, md: 158 },
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '3px',
                                    overflow: 'hidden',
                                    px: 2.5,
                                    py: 1.5,
                                    transition: `transform 0.5s ${tokens.ease}`,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={sponsor.img}
                                    alt={sponsor.name}
                                    // max-* invece di width/height 100%: dentro un grid
                                    // centrato, height:100% non risolve e il box prende
                                    // l'altezza intrinseca, sforando la placca.
                                    sx={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        width: 'auto',
                                        height: 'auto',
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    position: 'relative',
                                    fontFamily: tokens.display,
                                    fontSize: { xs: 24, md: 27 },
                                    lineHeight: 1.05,
                                    color: tokens.white,
                                    mt: 3,
                                }}
                            >
                                {sponsor.name}
                            </Box>
                            <Box
                                sx={{
                                    position: 'relative',
                                    fontFamily: tokens.body,
                                    fontWeight: 300,
                                    fontSize: 13.5,
                                    lineHeight: 1.5,
                                    color: tokens.muted,
                                    mt: 1,
                                }}
                            >
                                {sponsor.category}
                            </Box>

                            <Box
                                className="vg-sponsor-cta"
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    mt: 'auto',
                                    pt: 3,
                                    fontFamily: tokens.heading,
                                    fontSize: 12.5,
                                    letterSpacing: '0.18em',
                                    textTransform: 'uppercase',
                                    color: tokens.dim,
                                    transition: `all 0.4s ${tokens.ease}`,
                                }}
                            >
                                Visita il sito
                                <ArrowOutwardIcon sx={{ fontSize: 16 }} />
                            </Box>
                        </Link>
                    </Reveal>
                ))}

                {/* Open slot — remove this block if you don't want to advertise it. */}
                <Reveal delay={SponsorData.length * 100}>
                    <Box
                        onClick={scrollToContatti}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') scrollToContatti(); }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            height: '100%',
                            minHeight: { xs: 220, md: 300 },
                            cursor: 'pointer',
                            border: `1px dashed ${tokens.lineStrong}`,
                            p: { xs: 2.5, md: 3 },
                            transition: `all 0.45s ${tokens.ease}`,
                            ':hover': {
                                borderColor: tokens.red,
                                backgroundColor: 'rgba(224, 26, 34, 0.06)',
                                transform: 'translateY(-5px)',
                            },
                            ':hover .vg-slot-cta': { color: tokens.white, gap: '12px' },
                        }}
                    >
                        <Box
                            sx={{
                                fontFamily: tokens.display,
                                fontSize: { xs: 26, md: 30 },
                                lineHeight: 1.05,
                                color: tokens.white,
                            }}
                        >
                            DIVENTA PARTNER
                        </Box>
                        <Box
                            sx={{
                                fontFamily: tokens.body,
                                fontWeight: 300,
                                fontSize: 13.5,
                                lineHeight: 1.6,
                                color: tokens.muted,
                                mt: 1.5,
                            }}
                        >
                            Porta il tuo marchio sul ring insieme al Team Vivenzio.
                        </Box>
                        <Box
                            className="vg-slot-cta"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                mt: 3,
                                fontFamily: tokens.heading,
                                fontSize: 12.5,
                                letterSpacing: '0.18em',
                                textTransform: 'uppercase',
                                color: tokens.red,
                                transition: `all 0.4s ${tokens.ease}`,
                            }}
                        >
                            Contattaci
                            <ArrowOutwardIcon sx={{ fontSize: 16 }} />
                        </Box>
                    </Box>
                </Reveal>
            </Box>
        </Box>
    );
}
